import { RadiologyLiveIcon } from '@/components/icons/ProductIcons';
import { ChevronDown } from 'lucide-react';
import { useState } from 'react';

const audienceModes = [
  {
    key: 'individual',
    label: 'Individual',
    headline: 'Work faster with connected AI at your fingertips.',
    detail: 'Keep momentum from dictation to interpretation without jumping across disconnected tools.',
  },
  {
    key: 'group',
    label: 'Group',
    headline: 'Align workflows across your entire reading group.',
    detail: 'Combine reporting consistency and real-time clinical support in one connected environment.',
  },
  {
    key: 'department',
    label: 'Department',
    headline: 'Build a modern radiology operating layer.',
    detail: 'Move beyond one-off AI add-ons toward a unified workflow suite for tomorrow’s practice.',
  },
] as const;

export function HeroSection() {
  const [activeMode, setActiveMode] = useState<(typeof audienceModes)[number]['key']>('individual');
  const mode = audienceModes.find((m) => m.key === activeMode) ?? audienceModes[0];

  return (
    <section className="relative flex flex-col items-center justify-center h-screen px-4 scroll-snap-align-start">
      {/* Brand logo with ambient glow */}
      <div className="relative">
        <div className="absolute inset-0 rounded-full bg-primary/20 blur-3xl animate-glow-pulse" />
        <div className="relative z-10 w-20 h-20 rounded-2xl bg-card/50 border border-border/50 flex items-center justify-center">
          <RadiologyLiveIcon size={46} />
        </div>
      </div>

      <h1 className="mt-10 text-3xl sm:text-4xl md:text-5xl font-light text-foreground text-center tracking-tight">
        A connected AI suite for modern radiology.
      </h1>
      <p className="mt-4 max-w-2xl text-center text-base sm:text-lg text-muted-foreground">
        RadiologyLive.ai brings reporting, clinical insight, and workflow support into one unified platform.
      </p>

      <div className="mt-7 flex items-center gap-2 rounded-xl border border-border/60 bg-card/40 p-1">
        {audienceModes.map((item) => (
          <button
            key={item.key}
            type="button"
            onClick={() => setActiveMode(item.key)}
            className={`rounded-lg px-3 py-1.5 text-xs font-medium transition-colors ${
              activeMode === item.key
                ? 'bg-primary text-primary-foreground'
                : 'text-muted-foreground hover:text-foreground'
            }`}
          >
            {item.label}
          </button>
        ))}
      </div>
      <div className="mt-4 max-w-2xl text-center">
        <p className="text-base sm:text-lg text-foreground">{mode.headline}</p>
        <p className="mt-1 text-sm sm:text-base text-muted-foreground">{mode.detail}</p>
      </div>

      {/* Scroll indicator */}
      <div className="absolute bottom-12">
        <ChevronDown className="h-6 w-6 text-muted-foreground animate-chevron-bounce" />
      </div>
    </section>
  );
}
